import { useEffect, useState } from "react";
import { api, useApi } from "../api.js";
import { Notice, useToast } from "../components.js";
import { useStickyState } from "../useStickyState.js";
import {
  CollectionSummary,
  KnowledgeNote,
  SyllabusSelector,
  TargetTree,
  toggle,
  useGenerationTargets,
  type CategoryRow,
} from "./generation-targets.js";

/**
 * Content Generation (§22, Screen 3) — category-based generation.
 *
 * The hierarchy (board → class → subject → syllabus → chapter → sub_chapter →
 * topic) lives entirely in the main ZenMarks app, fetched live through
 * ZenForge's server as one nested tree per board/class/subject. A Collection
 * (knowledge base, model, categories) stays a purely ZenForge concept; this
 * screen is the one place the two combine, into jobs.
 *
 * Running a multi-step pipeline over the same targets is its own screen
 * (RunPipeline) rather than a mode toggle here. The two produce different
 * things — jobs against categories versus pipeline executions — and share only
 * the target picker, which is why that picker lives in generation-targets.tsx
 * and these two screens are otherwise separate.
 */
export function Generation() {
  const t = useGenerationTargets();
  const [selectedCategories, setSelectedCategories] = useState<Set<number>>(new Set());
  // Defaults on, but sticky: unticking it is a deliberate act and should hold
  // for this browser rather than resetting to on next visit. It publishes
  // straight to the main app with no review, so the choice is worth keeping.
  const [autoApprove] = useStickyState<boolean>("zf.gen.autoApprove", true);
  const [submitting, setSubmitting] = useState(false);
  const { toast, element: toastElement } = useToast();

  const categories = useApi<CategoryRow[]>(
    t.collectionId ? `/collections/${t.collectionId}/categories?activeOnly=true` : null,
    [t.collectionId],
  );

  const availableCategories = (categories.data ?? []).filter(
    (c) =>
      (c.categoryType ??
        (c.questionCategoryId ? "BOOK_BACK" : c.questionAssessmentType ? "BOOK_IN" : "MODULE")) !==
      "BOOK_BACK",
  );

  const jobCount = t.targetCount * selectedCategories.size;

  // Every active Module and Book-In category is selected by default
  useEffect(() => {
    setSelectedCategories(new Set(availableCategories.map((c) => c.id)));
  }, [categories.data]);

  async function generate() {
    if (!t.collectionId) {
      toast("Choose a collection first");
      return;
    }
    if (t.unmappedTargets.length > 0) {
      toast(`${t.unmappedTargets.length} selected target(s) have no PDF mapped`);
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.post<{
        created: number;
        skipped: number;
        skippedApproved: number;
      }>("/generation-jobs", {
        collectionId: t.collectionId,
        topicIds: [...t.selectedTopics],
        subChapterIds: [...t.selectedSubChapters],
        categoryIds: [...selectedCategories],
        autoApprove,
      });

      const notes = [
        result.skipped > 0 ? `${result.skipped} already queued` : "",
        result.skippedApproved > 0 ? `${result.skippedApproved} already approved` : "",
      ].filter(Boolean);
      const suffix = notes.length > 0 ? ` (${notes.join(", ")} skipped)` : "";
      toast(`Created ${result.created} job${result.created === 1 ? "" : "s"}${suffix}`);
      t.refresh();
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Failed to create jobs");
    } finally {
      setSubmitting(false);
    }
  }

  async function generateBookBack() {
    if (!t.collectionId) {
      toast("Choose a collection first");
      return;
    }
    if (!t.chapterId) {
      toast("Select a chapter to generate book-back questions");
      return;
    }
    if (selectedCategories.size === 0) {
      toast("Select at least one category");
      return;
    }
    setSubmitting(true);
    try {
      const result = await api.post<{
        created: number;
        skipped: number;
        skippedApproved: number;
      }>("/generation-jobs/book-back-chapter", {
        collectionId: t.collectionId,
        chapterId: t.chapterId,
        categoryIds: [...selectedCategories],
        autoApprove,
      });
      toast(`Queued single-batch book-back generation (${result.created} category jobs)`);
      t.refresh();
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Failed to queue book-back generation");
    } finally {
      setSubmitting(false);
    }
  }

  async function retryFailed() {
    try {
      const result = await api.post<{ retried: number }>("/generation-jobs/retry-failed", {});
      toast(
        result.retried === 0
          ? "No failed jobs to retry"
          : `Requeued ${result.retried} failed job${result.retried === 1 ? "" : "s"}`,
      );
      t.refresh();
    } catch (error: unknown) {
      toast(error instanceof Error ? error.message : "Retry failed");
    }
  }

  return (
    <>
      {t.apiError && (
        <Notice kind="error">
          <strong>Cannot load data:</strong> {t.apiError}
        </Notice>
      )}

      <div className="grid targets-sticky">
        <SyllabusSelector t={t} />
        <TargetTree t={t} onRetryFailed={() => void retryFailed()} />

        <div className="card span-5">
          <h2>Generate with</h2>

          <CollectionSummary t={t} />

          {t.collectionId && categories.loading && <p className="muted">Loading…</p>}
          {t.collectionId && categories.data?.length === 0 && (
            <Notice kind="warn">This collection has no active categories yet.</Notice>
          )}

          {availableCategories.map((category) => (
            <label
              key={category.id}
              className="book selectable row"
              style={{ cursor: "pointer", fontWeight: 400 }}
            >
              <input
                type="checkbox"
                checked={selectedCategories.has(category.id)}
                onChange={() => toggle(selectedCategories, category.id, setSelectedCategories)}
              />
              <span>
                <b>{category.name}</b>
                <div className="muted">
                  {category.code} · {category.responseType}
                </div>
              </span>
            </label>
          ))}

          <KnowledgeNote t={t} />

          <div className="row between" style={{ marginTop: 12 }}>
            <span className="muted">
              {t.targetCount} target{t.targetCount === 1 ? "" : "s"} selected
            </span>
          </div>

          <div className="row" style={{ marginTop: 12, gap: 8, flexWrap: "wrap" }}>
            <button
              type="button"
              className="btn primary"
              onClick={() => void generate()}
              disabled={
                jobCount === 0 || !t.collectionId || submitting || t.unmappedTargets.length > 0
              }
            >
              {submitting ? "Creating…" : "Generate Selected"}
            </button>
            <button
              type="button"
              className="btn"
              onClick={() => void generateBookBack()}
              disabled={
                !t.chapterId || !t.collectionId || submitting || selectedCategories.size === 0
              }
            >
              📖 Generate Chapter Book-Back
            </button>
          </div>
          <p className="muted" style={{ marginTop: 8 }}>
            Pairings that are already queued or approved are skipped, so pressing this twice will not
            double the work.
          </p>
        </div>
      </div>

      {toastElement}
    </>
  );
}
